import {
  FileText,
  GitBranch,
  LifeBuoy,
  ListChecks,
  Send,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Attenzione, H, P, Passi } from './pezzi';

/**
 * Il testo della guida di Mailing.
 *
 * Sta in un file suo, separato dalla finestra che lo mostra, per un motivo
 * pratico: il testo si corregge spesso, la finestra quasi mai. Chi viene a
 * cambiare una frase non deve passare in mezzo al codice dell'interfaccia.
 *
 * E' scritto per chi usa Pixel, non per chi lo sviluppa: niente gergo, e ogni
 * regola accompagnata dal motivo per cui c'e'. Una regola di cui non si capisce
 * il senso, prima o poi qualcuno prova ad aggirarla.
 */

export interface SezioneGuida {
  id: string;
  titolo: string;
  icona: typeof Send;
  /** La voce di menu a cui corrisponde: serve ad aprire la guida al punto
   *  giusto a seconda di dove ci si trova. */
  percorso?: string;
  contenuto: ReactNode;
}

export const SEZIONI_GUIDA: SezioneGuida[] = [
  {
    id: 'inizio',
    titolo: 'Da dove si comincia',
    icona: LifeBuoy,
    contenuto: (
      <div className="space-y-3">
        <P>
          Mailing serve a scrivere ai contatti dell'azienda: una comunicazione singola a tutta la
          lista, oppure una serie di email che partono da sole nei giorni successivi.
        </P>

        <H>Le cinque pagine</H>
        <P>
          <strong>Contatti</strong> e' la rubrica: chi c'e' e se possiamo scrivergli.{' '}
          <strong>Liste</strong> sono i gruppi di contatti a cui mandare una campagna.{' '}
          <strong>Campagne</strong> sono le comunicazioni che partono una volta sola.{' '}
          <strong>Sequenze</strong> sono i percorsi automatici. <strong>Modelli</strong> sono le
          email messe da parte per riusarle.
        </P>

        <H>Prova o invio vero</H>
        <P>
          Il sistema puo' stare in <strong>modalita' di prova</strong>: le campagne partono, le
          email si compongono per intero e le puoi leggere, ma non esce niente davvero. Quando e'
          in prova lo dice un avviso in cima alla pagina Campagne. Se l'avviso non c'e', le email
          partono davvero.
        </P>

        <H>Quante email al giorno</H>
        <P>
          Il tetto e' di <strong>100 email al giorno</strong>. Una campagna piu' grande non si
          perde: parte a scaglioni e riprende da sola il giorno dopo, finche' non ha finito.
        </P>
        <P>
          Non si spedisce di notte (dalle 20 alle 8) ne' nei fine settimana. Un invio che cade in
          quelle ore slitta alla prima ora buona.
        </P>
      </div>
    ),
  },

  {
    id: 'contatti',
    titolo: 'Contatti e consenso',
    icona: Users,
    percorso: '/mailing/contatti',
    contenuto: (
      <div className="space-y-3">
        <P>
          La rubrica si riempie da sola: chi si iscrive dal sito, i clienti presi da Fatture in
          Cloud, i lead. Puoi anche importare un file.
        </P>

        <H>Un contatto nuovo non riceve niente</H>
        <P>
          Nasce <strong>in attesa</strong>. Per diventare <strong>attivo</strong> serve che sia
          scritto da dove arriva e perche' possiamo scrivergli. Non e' una spunta da mettere:
          senza quella riga il sistema non lo fa passare.
        </P>
        <P>
          Il motivo e' semplice: se un domani qualcuno contesta, quella riga e' la risposta. Se non
          c'e', non c'e' risposta.
        </P>

        <H>Chi esce non rientra</H>
        <Attenzione>
          Chi si disiscrive, chi ci segnala come spam e gli indirizzi che rimbalzano finiscono in
          una lista di blocco. Reimportare il file <strong>non li riporta dentro</strong>, e non lo
          fa nemmeno la sincronizzazione dei clienti. E' voluto: e' la cosa che protegge il
          dominio.
        </Attenzione>

        <H>I colori accanto al nome</H>
        <P>
          Dicono in che stato e': <strong>attivo</strong> riceve, <strong>in attesa</strong> no,{' '}
          <strong>disiscritto</strong> o <strong>bloccato</strong> non ricevera' piu'.
        </P>
      </div>
    ),
  },

  {
    id: 'liste',
    titolo: 'Le liste',
    icona: ListChecks,
    percorso: '/mailing/liste',
    contenuto: (
      <div className="space-y-3">
        <P>
          Una lista e' un gruppo di contatti a cui mandare una campagna solo a loro: i clienti di un
          corso, chi hai conosciuto a una fiera, le persone da tenere aggiornate su una novita'. Un
          contatto puo' stare in piu' liste, e i contatti non sanno in quali liste sono.
        </P>

        <H>Creare una lista e riempirla</H>
        <Passi
          voci={[
            <>
              Nella scheda <strong>Liste</strong> premi <strong>Nuova lista</strong> e dalle un nome.
            </>,
            <>
              Si apre da sola la finestra <strong>Aggiungi contatti</strong>: cerca nella rubrica e
              spunta chi metterci. <strong>Prendi tutti i trovati</strong> spunta anche quelli non
              ancora mostrati.
            </>,
            <>
              Premi <strong>Aggiungi</strong>. Per aggiungerne altri dopo, apri la lista e premi di
              nuovo <strong>Aggiungi contatti</strong>.
            </>,
          ]}
        />
        <P>
          Ci sono anche tre scorciatoie in <strong>Contatti</strong>: spunti dei contatti e scegli{' '}
          <strong>Metti in una lista</strong> nella barra in basso; <strong>Nuovo contatto</strong>{' '}
          aggiunge un indirizzo e lo mette subito nelle liste che scegli; <strong>Importa
          contatti</strong> chiede, alla fine, se mettere tutto il file in una lista.
        </P>
        <Attenzione>
          Se una sequenza parte da quella lista, chi aggiungi ci entra subito e, se puo' ricevere,
          gli arrivano le sue email. La lista lo segnala con il nome della sequenza.
        </Attenzione>

        <H>Toglierli</H>
        <P>
          Apri la lista: premi la crocetta accanto a un contatto, oppure spuntane piu' di uno e
          premi <strong>Togli dalla lista</strong>. Per un contatto solo puoi anche aprirlo in{' '}
          <strong>Contatti</strong> e premere la crocetta accanto al nome della lista.
        </P>
        <P>
          Togliere qualcuno da una lista non lo cancella e non lo disiscrive: resta in rubrica,
          semplicemente non e' piu' in quel gruppo.
        </P>

        <H>Scaricare, rinominare o eliminare</H>
        <P>
          Apri la lista e usa <strong>Esporta Excel</strong>, <strong>Rinomina</strong> o{' '}
          <strong>Elimina</strong>. Eliminando una lista i contatti restano in rubrica.
        </P>
        <Attenzione>
          Una lista usata da una sequenza non si puo' eliminare: la sequenza smetterebbe di
          funzionare senza dirlo a nessuno. Prima toglila dalla sequenza.
        </Attenzione>

        <H>Liste e consenso sono due cose diverse</H>
        <P>
          Mettere qualcuno in una lista non gli da' il permesso di ricevere. Se un contatto e' in
          attesa o disiscritto, la campagna lo salta anche se e' nella lista. Per questo accanto a
          ogni lista vedi quanti contatti ha e quanti di questi possono ricevere.
        </P>
      </div>
    ),
  },

  {
    id: 'campagne',
    titolo: 'Mandare una campagna',
    icona: Send,
    percorso: '/mailing/campagne',
    contenuto: (
      <div className="space-y-3">
        <P>
          Una campagna e' una comunicazione che parte una volta sola: a tutti i contatti che
          possono ricevere, oppure solo ad alcune liste.
        </P>

        <H>Come si fa</H>
        <Passi
          voci={[
            <>
              <strong>Nuova campagna</strong>, dalle un nome. Il nome e' per te: non lo vede
              nessun altro.
            </>,
            <>
              Scrivi l'<strong>oggetto</strong>: e' quello che si legge nella posta in arrivo, e
              decide se la aprono o no.
            </>,
            <>
              Componi la email trascinando i blocchi (titolo, testo, immagine, bottone) dal
              pannello a sinistra. Oppure premi <strong>Parti da un modello</strong> e riusa una
              email gia' fatta.
            </>,
            <>
              Guarda l'<strong>Anteprima</strong>: e' esattamente quello che arrivera', non una
              simulazione.
            </>,
            <>
              In <strong>A chi la mandi?</strong> scegli <strong>A tutti</strong> oppure{' '}
              <strong>Solo ad alcune liste</strong>, e in quel caso spunta le liste. Sotto compare
              subito quante persone la riceveranno, e quante restano fuori perche' non hanno dato
              il consenso.
            </>,
            <>
              <strong>Invia</strong>. La finestra di conferma ripete a chi sta per andare. Da quel
              momento non si richiama indietro.
            </>,
          ]}
        />

        <Attenzione>
          Finche' non scegli a chi mandarla, la campagna <strong>non parte</strong>: il bottone
          Invia resta spento. E' voluto: mandare a tutti si puo', ma deve essere una scelta, non
          quello che succede se ci si dimentica di scegliere.
        </Attenzione>

        <H>Provare prima su di te</H>
        <P>
          Crea una lista con dentro solo te e chi deve controllare, e manda la campagna a quella.
          Vedi la email vera, nella tua casella, prima che la veda un cliente. Prima di inviare la
          prova premi <strong>Salva come modello</strong>: dopo crei la campagna vera con{' '}
          <strong>Parti da un modello</strong> e la mandi alla lista giusta, senza riscriverla.
        </P>

        <H>I segnaposto</H>
        <P>
          Scrivendo il testo puoi inserire <strong>Dati del destinatario</strong>: il nome,
          l'azienda, la citta'. Ognuno ricevera' la email con i suoi dati al posto giusto. Nel
          testo li vedi come{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">{'{{nome}}'}</code>, nell'anteprima li
          vedi gia' riempiti.
        </P>

        <H>Logo e piede si mettono da soli</H>
        <P>
          L'intestazione con il logo e il fondo con ragione sociale, sede, partita IVA e link di
          disiscrizione ci sono sempre. Non vanno scritti e non si possono togliere: sono
          obbligatori.
        </P>
      </div>
    ),
  },

  {
    id: 'sequenze',
    titolo: 'Le sequenze automatiche',
    icona: GitBranch,
    percorso: '/mailing/sequenze',
    contenuto: (
      <div className="space-y-3">
        <P>
          Una campagna parte una volta. Una sequenza e' un <strong>percorso</strong>: la persona ci
          entra per un motivo e da li' in poi avanza da sola. Manda, aspetta, guarda cos'e'
          successo, manda ancora.
        </P>

        <H>Si disegna su una lavagna</H>
        <P>
          Caselle collegate da frecce. Aggiungi una casella dai bottoni in alto, poi tiri la freccia
          dal pallino sotto una casella al pallino sopra la successiva.
        </P>
        <P>
          Le caselle sono sette: <strong>manda una email</strong>, <strong>aspetta</strong>,{' '}
          <strong>attendi</strong>, <strong>fai una domanda</strong> (divide il percorso in due,
          chi risponde si' di qua e chi no di la'), <strong>lista</strong>,{' '}
          <strong>ogni volta</strong>, <strong>fine</strong>.
        </P>
        <P>
          La casella <strong>lista</strong> fa una di tre cose: lascia andare avanti{' '}
          <strong>solo chi e' in una lista</strong> che scegli (gli altri escono dalla sequenza in
          quel punto), oppure <strong>mette</strong> o <strong>toglie</strong> il contatto da una
          lista.
        </P>
        <P>
          <strong>Attendi</strong> ferma il contatto finche' non viene messo in una lista (per
          esempio "richiesta inviata", che mette Pixel quando un congressista sceglie). Puoi dargli
          un tempo massimo: se scade, il contatto prende l'uscita <strong>Scaduto</strong> (un
          promemoria, di solito). Conta solo la lista messa dopo che il contatto e' arrivato li':
          una riconferma, che toglie e rimette la lista, lo sveglia di nuovo.
        </P>
        <P>
          <strong>Ogni volta</strong> e' una seconda partenza dentro la stessa sequenza: ogni volta
          che il contatto viene messo in una lista fa le caselle collegate sotto (per esempio
          "rimanda il link"), e lo lascia dov'era nel percorso. Vale per chi e' entrato nella
          sequenza.
        </P>

        <H>Chi ci entra</H>
        <P>
          Lo decidi quando la crei, e finche' e' spenta lo cambi cliccando la casella{' '}
          <strong>Si parte</strong>: chi <strong>da' il consenso</strong>, i contatti di{' '}
          <strong>una lista</strong>, oppure <strong>solo chi scegli tu</strong> a mano.
        </P>
        <P>
          Se parte da una lista scegli anche a chi: <strong>tutta la lista</strong> (accendendo
          entrano subito tutti quelli che ci sono, e poi chi aggiungi) oppure{' '}
          <strong>solo chi aggiungi da ora in poi</strong> (chi c'e' gia' non riceve niente, e
          quando metti qualcuno nella lista la sequenza parte solo per lui). Mettere qualcuno nella
          lista — a mano, dalla selezione o con un'importazione — basta perche' ci entri da solo.
        </P>

        <H>Promozioni o informazioni sul corso</H>
        <P>
          Una sequenza di <strong>promozioni e novita'</strong> arriva solo a chi ha accettato di
          ricevere le mail. Una sequenza di <strong>informazioni sul corso</strong> (date, sede,
          documenti, cosa portare) arriva anche agli iscritti che non hanno accettato le
          promozioni, ma mai a chi si e' disiscritto.
        </P>
        <P>
          In una sequenza di informazioni sul corso non vanno offerte ne' altri corsi: a chi non ha
          accettato le promozioni sarebbe spam. Il modo giusto e' una lista per ogni corso, con
          dentro gli iscritti, e una sequenza che parte da quella lista.
        </P>

        <H>Accendere</H>
        <P>
          Il bottone <strong>Accendi</strong> prima controlla. Se qualcosa non va — una email
          senza oggetto, un'attesa senza durata, un percorso che gira a vuoto — non accende niente
          e ti dice cosa manca.
        </P>
        <Attenzione>
          Una sequenza accesa <strong>non si modifica</strong>. Per cambiare il percorso mettila in
          pausa: chi e' dentro resta fermo dov'e' e riprende da li'.
        </Attenzione>

        <H>Ha risposto?</H>
        <P>
          Fra le domande c'e' anche <strong>ha risposto?</strong>: si' se la persona ha risposto
          alla email. La risposta non si perde e non cambia niente per chi la legge: il gestionale
          la inoltra alla solita casella, e rispondendo si scrive direttamente a chi ha scritto.
        </P>

        <H>Le aperture non si possono usare</H>
        <P>
          Fra le domande c'e' "ha cliccato" ma non "ha aperto". Non e' una dimenticanza: Apple Mail
          apre le email da solo per conto suo, quindi un'apertura puo' non essere di una persona.
          Un clic invece lo fa qualcuno.
        </P>
      </div>
    ),
  },

  {
    id: 'modelli',
    titolo: 'I modelli',
    icona: FileText,
    percorso: '/mailing/modelli',
    contenuto: (
      <div className="space-y-3">
        <P>
          Un modello e' una email messa da parte, con il suo oggetto e la sua impaginazione, da
          riusare invece di rifarla.
        </P>

        <H>Salvarne uno</H>
        <P>
          Da dove componi — una campagna, una casella di sequenza — premi{' '}
          <strong>Salva come modello</strong>. Gli dai un nome e dici dove vuoi che compaia:
          ovunque, solo nelle campagne e sequenze, oppure solo quando scrivi a un lead.
        </P>

        <H>Usarne uno</H>
        <P>
          <strong>Parti da un modello</strong> compare negli stessi posti, piu' la finestra di
          invio a un lead. Scegli dalla lista e vedi subito com'e' fatto prima di prenderlo.
        </P>

        <H>Quello che prendi e' una copia</H>
        <P>
          Se domani ritocchi un modello, la campagna nata da lui ieri non cambia. Se lo cancelli,
          non sparisce niente di gia' partito. Serve a questo: quello che e' stato mandato non deve
          poter cambiare dopo.
        </P>
        <P>
          Per lo stesso motivo, se hai gia' scritto un oggetto, il modello <strong>non te lo
          sovrascrive</strong>.
        </P>

        <H>Scrivere a un lead</H>
        <P>
          Nella scheda di un lead, <strong>Invia email</strong> ti lascia due strade: scrivere il
          testo a mano come sempre, oppure <strong>Usa un modello</strong> e mandare la email
          impaginata. Con il modello l'oggetto lo cambi ancora, il contenuto no.
        </P>
      </div>
    ),
  },

  {
    id: 'salute',
    titolo: 'Tenere la posta in salute',
    icona: ShieldCheck,
    contenuto: (
      <div className="space-y-3">
        <P>
          In cima alla pagina Campagne ci sono tre numeri. Sono la cosa piu' importante di tutta la
          sezione, e vanno guardati prima di ogni invio grosso.
        </P>

        <H>Reclami (ultimi 30 giorni)</H>
        <P>
          Quante persone ci hanno segnalato come spam. Sopra lo <strong>0,3%</strong> Gmail
          comincia a rifiutare tutto quello che parte dal nostro dominio, comprese le email di
          servizio e le fatture. E' il numero da tenere piu' d'occhio.
        </P>

        <H>Rimbalzi (ultimi 30 giorni)</H>
        <P>
          Indirizzi che non esistono piu'. Sopra il <strong>5%</strong> la reputazione del dominio
          peggiora. Di solito vuol dire che la lista e' vecchia.
        </P>

        <H>Inviabili oggi</H>
        <P>Quante email restano dentro il tetto giornaliero.</P>

        <H>Le tre regole che valgono sempre</H>
        <P>
          Scrivere <strong>solo a chi ha dato il consenso</strong>. Scrivere{' '}
          <strong>di cose attinenti</strong> a quello che quella persona tratta davvero. Scrivere{' '}
          <strong>quando si ha qualcosa da dire</strong>, non a calendario: la lista che riceve
          troppo e' quella che si disiscrive.
        </P>
      </div>
    ),
  },

  {
    id: 'problemi',
    titolo: 'Se qualcosa non va',
    icona: LifeBuoy,
    contenuto: (
      <div className="space-y-3">
        <H>"La campagna non parte"</H>
        <P>
          Guarda il suo stato. <strong>In invio</strong> vuol dire che sta partendo a scaglioni:
          con il tetto di 100 al giorno una lista grande ci mette qualche giorno, ed e' normale.
        </P>

        <H>"I destinatari sono meno di quanti pensavo"</H>
        <P>
          Ricevono solo i contatti <strong>attivi</strong>. Quelli in attesa non hanno un consenso
          registrato. Il numero degli esclusi lo trovi accanto a quello dei destinatari, quando
          premi Calcola destinatari.
        </P>

        <H>"Ho importato i clienti ma sono tutti in attesa"</H>
        <P>
          E' il comportamento giusto: importare non crea un consenso. Va registrato dichiarando da
          dove arriva e con quale motivo.
        </P>

        <H>"Non ricevo niente nemmeno io"</H>
        <P>
          Siamo in modalita' di prova: le email si compongono ma non escono. Le puoi leggere per
          intero, ma nessuna parte davvero.
        </P>

        <H>"Una sequenza si e' fermata"</H>
        <P>
          Succede quando la persona esce dalla lista — si disiscrive, segnala spam, l'indirizzo
          rimbalza. E' voluto: altrimenti la seconda email partirebbe lo stesso, ed e' proprio il
          caso che ci fa finire nella lista nera.
        </P>
      </div>
    ),
  },
];
